type NotificationColor = "blue" | "green" | "yellow" | "red";

interface NotificationBarProps {
  color: NotificationColor;
  children: React.ReactNode;
  onDismiss?: () => void;
}

const colorConfig: Record<NotificationColor, { bg: string; accent: string }> = {
  blue: {
    bg: "bg-notification-blue-bg",
    accent: "radial-gradient(ellipse at 67% 0%, #93C2FD 0%, #0167FF 100%)",
  },
  green: {
    bg: "bg-notification-green-bg",
    accent: "radial-gradient(ellipse at 67% 0%, #6EE7A0 0%, #0FA144 100%)",
  },
  yellow: {
    bg: "bg-notification-yellow-bg",
    accent: "radial-gradient(ellipse at 67% 0%, #FEE281 0%, #F6C002 100%)",
  },
  red: {
    bg: "bg-notification-red-bg",
    accent: "radial-gradient(ellipse at 67% 0%, #F2897D 0%, #E52B16 100%)",
  },
};

function DismissIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 1L11 11M11 1L1 11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function NotificationBar({
  color,
  children,
  onDismiss,
}: NotificationBarProps) {
  const { bg, accent } = colorConfig[color];

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative w-full flex items-center py-4 px-6 border-b border-black-50 motion-safe:animate-slide-in-up ${bg}`}
    >
      {/* 4px accent bar on left edge */}
      <div
        className="absolute left-0 top-0 h-full w-1 rounded-r-sm"
        style={{ background: accent }}
        aria-hidden="true"
      />

      {/* Content */}
      <div className="flex flex-1 items-center gap-2 text-body-md tracking-tight">
        {children}
      </div>

      {/* Dismiss button */}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss notification"
          className="ml-3 shrink-0 p-1 rounded text-grey-700 hover:text-text-primary motion-safe:transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <DismissIcon />
        </button>
      )}
    </div>
  );
}

export type { NotificationColor, NotificationBarProps };
